import { ActiveHours } from "./ActiveHours";
import { DEFAULT_LIMITS, MAX_LIMITS, type ActionKind, type AutomationSettings, type Cap } from "@gf/shared";
import { Badge, Button, Callout, Card, Field, Skeleton, Switch, Tooltip, cx, useToast } from "@gf/ui";
import { RotateCcw, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { IgScopeNote } from "../../components/accounts/IgScopeNote";
import { RunStatusBadge } from "../../components/accounts/IgRunStatus";
import { NumberInput } from "../../components/NumberInput";
import { SaveBar, useUnsavedChangesGuard } from "../../components/SaveBar";
import { useIgAccount } from "../../lib/igAccount";
import { normalizeAutomation, useSaveSetting, useSettings, useSettingsScope } from "./useSettings";
import { PauseAlerts } from "./PauseAlerts";

/** Labels for the caps table. Unknown kinds (added to the contract later) fall back to a prettified key. */
const ACTION_META: Partial<Record<ActionKind, { label: string; hint: string }>> = {
  follow: { label: "Follows", hint: "Following new people" },
  unfollow: { label: "Unfollows", hint: "Cleaning up who you follow" },
  like: { label: "Likes", hint: "Liking posts" },
  comment: { label: "Comments", hint: "Comments posted for you" },
  story_view: { label: "Story views", hint: "Watching stories" },
  dm_new: { label: "New DMs", hint: "First messages to people you haven't talked to" },
  dm_reply: { label: "DM replies", hint: "Replies inside existing conversations" },
  publish: { label: "Posts published", hint: "Scheduled posts going live" },
  edit_post: { label: "Post edits", hint: "Auto-Caption updating captions and hashtags on your posts" },
};

function actionLabel(kind: string): { label: string; hint: string } {
  const meta = ACTION_META[kind as ActionKind];
  if (meta) return meta;
  const pretty = kind.replace(/_/g, " ");
  return { label: pretty.charAt(0).toUpperCase() + pretty.slice(1), hint: "" };
}

function sameJson(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function AutomationTab() {
  const settings = useSettings();
  const { igAccountId } = useSettingsScope();
  if (settings.isPending) {
    return (
      <div className="gf-stack">
        <Skeleton height={110} radius={10} />
        <Skeleton height={420} radius={10} />
      </div>
    );
  }
  if (settings.isError) {
    return (
      <Callout tone="danger" title="Couldn't load your automation settings" action={<Button size="sm" onClick={() => void settings.refetch()}>Try again</Button>}>
        {settings.error instanceof Error ? settings.error.message : null}
      </Callout>
    );
  }
  // Keyed by account: switching accounts starts a fresh form, so one account's draft never saves into another.
  return <AutomationForm key={igAccountId ?? "user"} initial={settings.data.automation} />;
}

function AutomationForm({ initial }: { initial: AutomationSettings }) {
  const toast = useToast();
  const save = useSaveSetting<AutomationSettings>("automation");
  const { current } = useIgAccount();
  const [draft, setDraft] = useState<AutomationSettings>(initial);
  const [saved, setSaved] = useState<AutomationSettings>(initial);

  // Pick up server changes (e.g. another tab) when we have no local edits.
  useEffect(() => {
    setSaved(initial);
    setDraft((d) => (sameJson(d, saved) ? initial : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const dirty = !sameJson(draft, saved);
  useUnsavedChangesGuard(dirty);

  const kinds = Object.keys(MAX_LIMITS) as ActionKind[];
  const delayError = draft.minDelaySec > draft.maxDelaySec ? "The shortest pause can't be longer than the longest." : undefined;
  const isRecommended = kinds.every((k) => sameJson(draft.limits[k], DEFAULT_LIMITS[k]));

  function patch(p: Partial<AutomationSettings>) {
    setDraft((d) => ({ ...d, ...p }));
  }
  function setCap(kind: ActionKind, field: keyof Cap, value: number) {
    setDraft((d) => ({ ...d, limits: { ...d.limits, [kind]: { ...d.limits[kind], [field]: value } } }));
  }

  async function onSave() {
    if (delayError) return;
    try {
      const res = await save.mutateAsync(draft);
      const next = normalizeAutomation(res.value);
      setSaved(next);
      setDraft(next);
      toast.success("Automation settings saved");
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <div className="gf-stack gfp-automation">
      <IgScopeNote />
      <Card accent className="gfp-master">
        <div className="gfp-master__row">
          <div className="gfp-master__text">
            <h2 className="gfp-master__title">{draft.enabled ? "Automation is on" : "Automation is off"}</h2>
            <p className="gfp-master__desc">
              {draft.enabled
                ? "GramFather can follow, like, comment and DM for you, within the limits below."
                : "GramFather won't take a single action on Instagram. Your campaigns and settings stay put until you switch it back on."}
            </p>
            {current ? (
              <div className="gfp-master__engine">
                <span className="gf-muted">@{current.username}</span>
                <RunStatusBadge account={current} />
              </div>
            ) : null}
          </div>
          <Switch size="lg" checked={draft.enabled} onChange={(v) => patch({ enabled: v })} aria-label="Automation master switch" />
        </div>
      </Card>

      <Callout tone="warn" icon={<ShieldAlert size={18} />} title="Instagram watches for robots">
        Act too fast or too often and Instagram blocks actions for a day, sometimes longer. Start new or quiet accounts on the
        recommended limits and raise them slowly over a few weeks. If Instagram pushes back anyway, GramFather pauses everything
        on its own.
      </Callout>

      <Card
        title="Daily and hourly limits"
        subtitle="Per Instagram account. GramFather never goes above these, and never above the hard ceiling."
        padding="none"
        actions={
          <Button
            size="sm"
            variant="ghost"
            icon={<RotateCcw size={15} />}
            disabled={isRecommended}
            onClick={() => patch({ limits: { ...DEFAULT_LIMITS } })}
          >
            Reset to recommended
          </Button>
        }
      >
        <div className="gfp-caps" role="table" aria-label="Action limits">
          <div className="gfp-caps__row gfp-caps__row--head" role="row">
            <span role="columnheader">Action</span>
            <span role="columnheader">Per day</span>
            <span role="columnheader">Per hour</span>
            <span role="columnheader">
              <span className="gf-sr-only">Status</span>
            </span>
          </div>
          {kinds.map((kind) => {
            const meta = actionLabel(kind);
            const cap = draft.limits[kind] ?? DEFAULT_LIMITS[kind];
            const max = MAX_LIMITS[kind];
            const rec = DEFAULT_LIMITS[kind];
            const above = cap.perDay > rec.perDay || cap.perHour > rec.perHour;
            const hourOverDay = cap.perHour > cap.perDay;
            return (
              <div className={cx("gfp-caps__row", above && "gfp-caps__row--above")} role="row" key={kind}>
                <span className="gfp-caps__action" role="cell">
                  <span className="gfp-caps__label">{meta.label}</span>
                  {meta.hint ? <span className="gfp-caps__hint">{meta.hint}</span> : null}
                </span>
                <span role="cell">
                  <NumberInput
                    size="sm"
                    value={cap.perDay}
                    min={0}
                    max={max.perDay}
                    onChange={(v) => setCap(kind, "perDay", v)}
                    aria-label={`${meta.label} per day`}
                    suffix={<span className="gfp-caps__max">max {max.perDay}</span>}
                  />
                </span>
                <span role="cell">
                  <NumberInput
                    size="sm"
                    value={cap.perHour}
                    min={0}
                    max={max.perHour}
                    onChange={(v) => setCap(kind, "perHour", v)}
                    aria-label={`${meta.label} per hour`}
                    suffix={<span className="gfp-caps__max">max {max.perHour}</span>}
                  />
                </span>
                <span role="cell" className="gfp-caps__status">
                  {cap.perDay === 0 ? (
                    <Badge size="sm">Off</Badge>
                  ) : above ? (
                    <Tooltip content={`Recommended: ${rec.perDay} a day, ${rec.perHour} an hour`}>
                      <Badge size="sm" tone="warn" tabIndex={0}>
                        Above recommended
                      </Badge>
                    </Tooltip>
                  ) : hourOverDay ? (
                    <Badge size="sm" tone="info">
                      Daily cap wins
                    </Badge>
                  ) : (
                    <Badge size="sm" tone="success">
                      Safe
                    </Badge>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="gfp-settings-grid">
        <Card title="Pacing" subtitle="A random pause between every action keeps you looking human.">
          <div className="gf-stack">
            <div className="gfp-two">
              <Field label="Shortest pause" error={delayError}>
                <NumberInput value={draft.minDelaySec} min={5} max={3600} onChange={(v) => patch({ minDelaySec: v })} suffix="sec" />
              </Field>
              <Field label="Longest pause">
                <NumberInput value={draft.maxDelaySec} min={5} max={3600} onChange={(v) => patch({ maxDelaySec: v })} suffix="sec" />
              </Field>
            </div>
            <Switch
              labelPosition="start"
              checked={draft.allowBackgroundTab}
              onChange={(v) => patch({ allowBackgroundTab: v })}
              label="Open Instagram in a background tab"
              description="When no Instagram tab is open, the extension may open a pinned one so automations keep running."
            />
          </div>
        </Card>

      </div>

      <ActiveHours value={draft} onChange={patch} />

      <PauseAlerts />

      <SaveBar
        dirty={dirty}
        saving={save.isPending}
        disabled={!!delayError}
        onSave={() => void onSave()}
        onDiscard={() => setDraft(saved)}
      />
    </div>
  );
}
